import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Grid,
  Container,
  Card,
  Fade,
  Collapse,
  Divider,
  useTheme,
  useMediaQuery,
  styled,
} from '@mui/material';
import {
  Shield,
  DollarSign,
  CheckCircle,
  Sparkles,
  Wifi,
  Globe,
  Tag,
  ArrowRight,
  ArrowLeft
} from 'lucide-react';
import { motion } from 'framer-motion';
import './DynamicSavingsBudget.css';

// Styled Components
const InfoCard = styled(Card)(({ variant = 'mvno', theme }) => ({
  background: variant === 'mvno' 
    ? 'linear-gradient(135deg, #f7f7f7, #e5e5e5)' 
    : `linear-gradient(135deg, ${variant === 'premium-pink' ? '#FF4D8D' : '#4F46E5'}, ${variant === 'premium-pink' ? '#FF3377' : '#4338CA'})`,
  color: variant === 'mvno' ? '#333' : '#fff',
  height: '100%',
  position: 'relative',
  overflow: 'hidden',
  borderRadius: '24px',
  boxShadow: variant === 'mvno' 
    ? '0 8px 20px rgba(0, 0, 0, 0.05), 0 0 0 1px rgba(0, 0, 0, 0.05)' 
    : '0 15px 30px rgba(79, 70, 229, 0.2)',
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: variant === 'mvno'
      ? '0 15px 30px rgba(0, 0, 0, 0.1), 0 0 0 1px rgba(0, 0, 0, 0.07)'
      : '0 20px 40px rgba(79, 70, 229, 0.3)'
  },
  padding: theme.spacing(3),
}));

// Improved table styling for better alignment and readability
const StyledTableRow = styled(TableRow)(({ theme, isPlan }) => ({
  position: 'relative',
  transition: 'all 0.3s ease',
  '&:hover': { 
    backgroundColor: 'rgba(79,70,229,0.04)',
    transform: 'scale(1.01)',
    boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
    zIndex: 10
  },
  '&:hover td': {
    color: '#4F46E5'
  },
  '&:hover td:first-of-type': {
    borderLeft: '3px solid #4F46E5'
  },
  ...(isPlan && {
    backgroundColor: 'rgba(79,70,229,0.04)',
  }),
  '&:nth-of-type(even)': {
    backgroundColor: 'rgba(249, 250, 251, 0.5)',
  },
}));

const StyledTableCell = styled(TableCell)(({ theme, align, variant }) => ({
  padding: theme.spacing(1.5, 2),
  fontSize: '0.95rem',
  borderBottom: '1px solid rgba(0,0,0,0.05)',
  color: variant === 'strikethrough' ? '#666' : (variant === 'savings' ? '#059669' : (variant === 'discount' ? '#4F46E5' : '#333')),
  textDecoration: variant === 'strikethrough' ? 'line-through' : 'none',
  textAlign: align || 'left',
  transition: 'all 0.2s ease',
  height: '72px', // Fixed height for consistency
  verticalAlign: 'middle', // Center content vertically
  fontWeight: variant === 'discount' ? 600 : (variant === 'savings' ? 500 : 400),
  
  // Special handling for price columns to ensure consistent alignment
  ...(align === 'right' && {
    paddingRight: theme.spacing(3),
  }),
  
  // Responsive adjustments
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(1, 1.5),
    fontSize: '0.85rem',
    height: '60px',
  },
}));

// Price cell component to ensure consistent rendering
const PriceCell = ({ children, variant, align = 'right', ...props }) => (
  <StyledTableCell 
    variant={variant} 
    align={align} 
    {...props}
  >
    <Typography 
      sx={{ 
        fontWeight: variant === 'discount' ? 600 : 400,
        fontSize: variant === 'discount' ? '1.1rem' : '0.95rem',
        display: 'inline-block',
        width: '100%',
        textAlign: 'right',
        color: variant === 'discount' ? '#4F46E5' : 'inherit'
      }}
    >
      {children}
    </Typography>
  </StyledTableCell>
);

// Blob Background Element
const BlobBackground = () => {
  return (
    <>
      <Box 
        className="ds-blob ds-blob--primary"
        sx={{
          position: 'absolute',
          top: 0,
          right: 0,
          width: '600px',
          height: '600px',
          opacity: 0.04,
          zIndex: 0,
          pointerEvents: 'none',
          filter: 'blur(80px)',
          background: 'radial-gradient(circle at center, #4F46E5, transparent 60%)',
        }}
      />
      <Box 
        className="ds-blob ds-blob--secondary"
        sx={{
          position: 'absolute',
          top: '-200px',
          left: '-200px',
          width: '500px',
          height: '500px',
          opacity: 0.05,
          zIndex: 0,
          filter: 'blur(60px)',
          background: 'radial-gradient(circle at center, #FF4D8D, transparent 70%)',
        }}
      />
    </>
  );
};

// Feature icon with label - improved spacing for readability
const FeatureItem = ({ icon: Icon, text, color = '#4F46E5', bgColor = 'rgba(79, 70, 229, 0.1)' }) => (
  <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1.5 }} className="ds-feature-item">
    <Box sx={{ 
      minWidth: 32, 
      height: 32, 
      borderRadius: '50%', 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center',
      backgroundColor: bgColor,
      mr: 1.5
    }}>
      <Icon size={16} color={color} />
    </Box>
    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
      {text}
    </Typography>
  </Box>
);

// Budget/MVNO Savings Component
const DynamicSavingsBudget = ({ onBack }) => {
  const navigate = useNavigate();
  const [selectedMvno, setSelectedMvno] = useState('mint');
  const [selectedMvnoPlan, setSelectedMvnoPlan] = useState('Mint 4GB');
  const [selectedPremiumCarrier, setSelectedPremiumCarrier] = useState('verizon');
  const [selectedPremiumPlan, setSelectedPremiumPlan] = useState('Unlimited Welcome');
  const [showValueDetails, setShowValueDetails] = useState(false);
  const [animateCards, setAnimateCards] = useState(false);
  const [tableLoaded, setTableLoaded] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Navigation handler for the View All Circles button
  const handleViewAllCircles = () => {
    navigate('/all-circles');
  };

  // Premium Carrier Plans data model - UPDATED WITH LATEST PLANS
  const CARRIER_PLANS = {
    verizon: [
      { 
        name: 'Unlimited Welcome', 
        regularPrice: 65, 
        bestGroupPrice: 40, 
        data: 'Unlimited', 
        hotspot: '5GB', 
        priorityData: 'Standard',
        perks: ['6 months Disney+', '6 months Apple Music', 'Verizon Up Rewards', '$500 welcome credit'],
        international: 'Talk & Text to Mexico & Canada',
        coverage: 'Nationwide 5G & 4G LTE',
        valueScore: 80
      },
      { 
        name: 'Unlimited Plus', 
        regularPrice: 80, 
        bestGroupPrice: 55, 
        data: 'Unlimited', 
        hotspot: '30GB', 
        priorityData: 'Premium (50GB)',
        perks: ['Disney+ included', 'Apple Music 6 months', 'Verizon Up Rewards', '$500 welcome credit', 'Cloud Storage'],
        international: 'Talk, Text, Data in Mexico & Canada',
        coverage: 'Nationwide 5G & 4G LTE',
        valueScore: 90
      },
      { 
        name: 'Unlimited Ultimate', 
        regularPrice: 90, 
        bestGroupPrice: 65, 
        data: 'Unlimited', 
        hotspot: '60GB', 
        priorityData: 'Premium (Unlimited)',
        perks: ['Disney+, Hulu, ESPN+ included', 'Apple Music included', 'Verizon Up Rewards', '$500 welcome credit', '600GB Cloud Storage', 'Apple One 6 months'],
        international: 'Talk, Text, Data in Mexico & Canada + International Day Pass',
        coverage: 'Nationwide 5G Ultra Wideband & 4G LTE',
        valueScore: 98
      }
    ],
    tmobile: [
      { 
        name: 'Essentials', 
        regularPrice: 60, 
        bestGroupPrice: 26.25, 
        data: 'Unlimited', 
        hotspot: '5GB', 
        priorityData: 'Standard',
        perks: ['T-Mobile Tuesdays', '$350 welcome credit', 'Scam Shield'],
        international: 'Talk, Text to Mexico & Canada',
        coverage: 'Nationwide 5G & 4G LTE',
        valueScore: 75
      },
      { 
        name: 'Go5G', 
        regularPrice: 75, 
        bestGroupPrice: 38.75, 
        data: 'Unlimited', 
        hotspot: '15GB', 
        priorityData: 'Premium (100GB)',
        perks: ['T-Mobile Tuesdays', 'Netflix Basic 1-screen', '$350 welcome credit', 'Scam Shield', 'Free In-flight Wi-Fi', 'Free Texting Worldwide'],
        international: 'Talk, Text, Data in Mexico & Canada',
        coverage: 'Nationwide 5G & 4G LTE',
        valueScore: 85
      },
      { 
        name: 'Go5G Plus', 
        regularPrice: 90, 
        bestGroupPrice: 46.25, 
        data: 'Unlimited', 
        hotspot: '40GB', 
        priorityData: 'Premium (Unlimited)',
        perks: ['T-Mobile Tuesdays', 'Netflix Standard 2-screen', '$350 welcome credit', 'Scam Shield', 'Free In-flight Wi-Fi', 'Free Texting Worldwide', 'Apple TV+ included', '1 year of Paramount+ included'],
        international: 'Talk, Text, Data in Mexico & Canada + 5GB high-speed data in 215+ destinations',
        coverage: 'Nationwide 5G & 4G LTE',
        valueScore: 92
      },
      { 
        name: 'Go5G Next', 
        regularPrice: 100, 
        bestGroupPrice: 56.25, 
        data: 'Unlimited', 
        hotspot: '50GB', 
        priorityData: 'Premium (Unlimited)',
        perks: ['T-Mobile Tuesdays', 'Netflix Standard 2-screen', '$350 welcome credit', 'Scam Shield', 'Free In-flight Wi-Fi', 'Free Texting Worldwide', 'Apple TV+ included', '1 year of Paramount+ included', 'Annual phone upgrades'],
        international: 'Talk, Text, Data in Mexico & Canada + 5GB high-speed data in 215+ destinations',
        coverage: 'Nationwide 5G & 4G LTE',
        valueScore: 95
      }
    ],
    att: [
      { 
        name: 'Unlimited Starter SL', 
        regularPrice: 65, 
        bestGroupPrice: 35.99, 
        data: 'Unlimited', 
        hotspot: 'None', 
        priorityData: 'Standard',
        perks: ['$450 welcome credit', 'AT&T ActiveArmor security', '5G Access'],
        international: 'Talk, Text to Mexico & Canada',
        coverage: 'Nationwide 5G & 4G LTE',
        valueScore: 70
      },
      { 
        name: 'Unlimited Extra EL', 
        regularPrice: 75, 
        bestGroupPrice: 45, 
        data: 'Unlimited', 
        hotspot: '15GB', 
        priorityData: 'Premium (50GB)',
        perks: ['$450 welcome credit', 'AT&T ActiveArmor security', '5G Access', '3 months HBO Max'],
        international: 'Talk, Text, Data in Mexico & Canada',
        coverage: 'Nationwide 5G & 4G LTE',
        valueScore: 82
      },
      { 
        name: 'Unlimited Premium PL', 
        regularPrice: 85.99, 
        bestGroupPrice: 55, 
        data: 'Unlimited', 
        hotspot: '30GB', 
        priorityData: 'Premium (Unlimited)',
        perks: ['HBO Max included', '$450 welcome credit', 'AT&T ActiveArmor security', '5G Access', 'HD Streaming', '100GB Cloud Storage'],
        international: 'Talk, Text, Data in Mexico & Canada + International Day Pass discounts',
        coverage: 'Nationwide 5G & 4G LTE',
        valueScore: 90
      }
    ]
  };

  // MVNO/Prepaid Plans Data - UPDATED WITH MORE OPTIONS
  const MVNO_PLANS = {
    mint: [
      { name: 'Mint 4GB', price: 15, data: '4GB', hotspot: '4GB', international: 'Calls to Mexico & Canada', priorityData: 'Deprioritized', coverage: 'T-Mobile Network' },
      { name: 'Mint 10GB', price: 20, data: '10GB', hotspot: '10GB', international: 'Calls to Mexico & Canada', priorityData: 'Deprioritized', coverage: 'T-Mobile Network' },
      { name: 'Mint 15GB', price: 25, data: '15GB', hotspot: '15GB', international: 'Calls to Mexico & Canada', priorityData: 'Deprioritized', coverage: 'T-Mobile Network' },
      { name: 'Mint Unlimited', price: 30, data: '35GB then throttled', hotspot: '5GB', international: 'Calls to Mexico & Canada', priorityData: 'Deprioritized', coverage: 'T-Mobile Network' }
    ],
    cricket: [
      { name: 'Cricket 5GB', price: 30, data: '5GB', hotspot: 'None', international: 'None', priorityData: 'Deprioritized', coverage: 'AT&T Network' },
      { name: 'Cricket 10GB', price: 40, data: '10GB', hotspot: 'None', international: 'Calls to Mexico & Canada', priorityData: 'Deprioritized', coverage: 'AT&T Network' },
      { name: 'Cricket Unlimited', price: 55, data: 'Unlimited (8Mbps cap)', hotspot: '15GB (+$5/mo)', international: 'Mexico & Canada usage', priorityData: 'Deprioritized', coverage: 'AT&T Network' },
      { name: 'Cricket Unlimited+', price: 60, data: 'Unlimited (No speed cap)', hotspot: '15GB', international: 'Mexico & Canada usage + texting to 37 countries', priorityData: 'Deprioritized', coverage: 'AT&T Network' }
    ],
    visible: [
      { name: 'Visible', price: 25, data: 'Unlimited', hotspot: 'Unlimited (5Mbps)', international: 'Calls to Mexico & Canada', priorityData: 'Deprioritized', coverage: 'Verizon Network' },
      { name: 'Visible+', price: 45, data: 'Unlimited', hotspot: '50GB Premium', international: 'Calls & data in Mexico & Canada', priorityData: 'Priority up to 50GB', coverage: 'Verizon Network' }
    ],
    boost: [
      { name: 'Boost 1GB', price: 10, data: '1GB', hotspot: 'Shared with data', international: 'None', priorityData: 'Deprioritized', coverage: 'T-Mobile Network' },
      { name: 'Boost 5GB', price: 25, data: '5GB', hotspot: 'Shared with data', international: 'None', priorityData: 'Deprioritized', coverage: 'T-Mobile Network' },
      { name: 'Boost Unlimited', price: 50, data: '35GB then throttled', hotspot: '12GB', international: 'Calls to Mexico', priorityData: 'Deprioritized', coverage: 'T-Mobile Network' }
    ],
    metro: [
      { name: 'Metro 5GB', price: 30, data: '5GB', hotspot: 'Shared with data', international: 'None', priorityData: 'Deprioritized', coverage: 'T-Mobile Network' },
      { name: 'Metro Unlimited', price: 40, data: '35GB then throttled', hotspot: '5GB', international: 'Talk to Mexico & Canada', priorityData: 'Deprioritized', coverage: 'T-Mobile Network' },
      { name: 'Metro Unlimited+', price: 50, data: '35GB then throttled', hotspot: '15GB', international: 'Talk & text to Mexico & Canada', priorityData: 'Deprioritized', coverage: 'T-Mobile Network' }
    ],
    googlefi: [
      { name: 'Fi Flexible', price: 20, data: 'Pay-per-GB ($10/GB)', hotspot: 'Shared with data', international: 'Free texts, $0.20/min calls', priorityData: 'Standard', coverage: 'T-Mobile & US Cellular Networks' },
      { name: 'Fi Unlimited', price: 65, data: '22GB then throttled', hotspot: 'Shared with data', international: 'Free texts, data, $0.20/min calls', priorityData: 'Standard', coverage: 'T-Mobile & US Cellular Networks' },
      { name: 'Fi Unlimited Plus', price: 75, data: '50GB then throttled', hotspot: 'Shared with data', international: 'Free texts, data, $0.20/min calls, international hotspot', priorityData: 'Standard', coverage: 'T-Mobile & US Cellular Networks' }
    ],
    usmobile: [
      { name: 'US Mobile Custom', price: 8, data: 'Pay-per-GB', hotspot: 'Shared with data', international: 'None', priorityData: 'Deprioritized', coverage: 'Verizon or T-Mobile Network' },
      { name: 'US Mobile Unlimited Basic', price: 35, data: '30GB then throttled', hotspot: 'Shared with data', international: 'Free calls to 10+ countries', priorityData: 'Deprioritized', coverage: 'Verizon or T-Mobile Network' },
      { name: 'US Mobile Unlimited Premium', price: 45, data: '100GB then throttled', hotspot: '50GB', international: 'Free calls to 10+ countries, eSIM in 165+ countries', priorityData: 'Priority data', coverage: 'Verizon or T-Mobile Network' }
    ],
    consumer: [
      { name: 'Consumer 1GB', price: 20, data: '1GB', hotspot: 'Shared with data', international: 'None', priorityData: 'Deprioritized', coverage: 'AT&T or T-Mobile Network' },
      { name: 'Consumer 5GB', price: 30, data: '5GB', hotspot: 'Shared with data', international: 'None', priorityData: 'Deprioritized', coverage: 'AT&T or T-Mobile Network' },
      { name: 'Consumer Unlimited', price: 55, data: 'Unlimited', hotspot: 'Shared with data', international: 'None', priorityData: 'Deprioritized', coverage: 'AT&T or T-Mobile Network' }
    ]
  };

  // Get current premium plan details
  const getCurrentPremiumPlan = () => {
    const plans = CARRIER_PLANS[selectedPremiumCarrier];
    return plans.find(plan => plan.name === selectedPremiumPlan) || plans[0];
  };

  // Get current MVNO plan details
  const getCurrentMvnoPlan = () => {
    const plans = MVNO_PLANS[selectedMvno];
    return plans.find(plan => plan.name === selectedMvnoPlan) || plans[0];
  };

  // ===== MVNO VALUE CALCULATION =====
  
  // Calculate the monthly price difference between MVNO and premium
  const getMonthlyDifference = () => {
    const mvnoPlan = getCurrentMvnoPlan();
    const premiumPlan = getCurrentPremiumPlan();
    return premiumPlan.bestGroupPrice - mvnoPlan.price;
  };

  // Calculate added value per month for premium plans
  const getMonthlyAddedValue = () => {
    const premiumPlan = getCurrentPremiumPlan();
    
    // Base value from perks
    let perkValue = 0;
    
    // Streaming services values
    if (premiumPlan.perks.some(p => p.includes('Disney+'))) perkValue += 8;
    if (premiumPlan.perks.some(p => p.includes('Netflix'))) perkValue += 10;
    if (premiumPlan.perks.some(p => p.includes('Apple Music'))) perkValue += 10;
    if (premiumPlan.perks.some(p => p.includes('HBO Max'))) perkValue += 15;
    if (premiumPlan.perks.some(p => p.includes('Hulu'))) perkValue += 8;
    if (premiumPlan.perks.some(p => p.includes('ESPN+'))) perkValue += 7;
    if (premiumPlan.perks.some(p => p.includes('Apple TV+'))) perkValue += 7;
    if (premiumPlan.perks.some(p => p.includes('Paramount+'))) perkValue += 5;
    if (premiumPlan.perks.some(p => p.includes('Apple One'))) perkValue += 17;
    
    // Welcome credits amortized over 24 months
    const welcomeCredit = premiumPlan.perks.find(p => p.includes('welcome credit'));
    if (welcomeCredit) {
      const amount = parseInt(welcomeCredit.match(/\$(\d+)/)[1]);
      perkValue += amount / 24; // Divided over 2 years
    }
    
    // Network quality and priority data value
    let networkValue = 0;
    if (premiumPlan.priorityData.includes('Premium')) networkValue += 15;
    
    // International roaming value
    let internationalValue = 0;
    if (premiumPlan.international.includes('Mexico & Canada')) internationalValue += 5;
    if (premiumPlan.international.includes('destinations')) internationalValue += 15;
    
    // Hotspot value
    const hotspotGB = parseInt(premiumPlan.hotspot) || 0;
    const hotspotValue = hotspotGB > 0 ? Math.min(20, hotspotGB / 2) : 0;
    
    return Math.round(perkValue + networkValue + internationalValue + hotspotValue);
  };

  // Calculate the total value ratio (what you get vs what you pay)
  const getValueRatio = () => {
    const premiumPlan = getCurrentPremiumPlan();
    const totalPremiumValue = premiumPlan.bestGroupPrice + getMonthlyAddedValue();
    return (totalPremiumValue / premiumPlan.bestGroupPrice).toFixed(1);
  };

  // Get the total monthly value in dollars
  const getTotalMonthlyValue = () => {
    const premiumPlan = getCurrentPremiumPlan();
    return premiumPlan.bestGroupPrice + getMonthlyAddedValue();
  };

  // MVNO selection handler
  const handleMvnoSelect = (mvno) => {
    setSelectedMvno(mvno);
    setSelectedMvnoPlan(MVNO_PLANS[mvno][0].name);
  };

  // Premium carrier selection handler
  const handleCarrierSelect = (carrier) => {
    setSelectedPremiumCarrier(carrier);
    setSelectedPremiumPlan(CARRIER_PLANS[carrier][0].name);
  };

  // Initialize animations
  useEffect(() => {
    // Animate cards after a short delay
    const timer = setTimeout(() => {
      setAnimateCards(true);
    }, 300);
    
    // Animate table after cards
    const tableTimer = setTimeout(() => {
      setTableLoaded(true);
    }, 600);
    
    return () => {
      clearTimeout(timer);
      clearTimeout(tableTimer);
    };
  }, []);

  return (
    <Box 
      className="ds-container ds-mvno-container"
      sx={{ 
        background: 'linear-gradient(135deg, rgba(255,255,255,1) 0%, rgba(249,245,255,1) 100%)',
        position: 'relative',
        pt: 6,
        pb: 8,
        overflow: 'hidden',
      }}
    >
      <BlobBackground />
      
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
        {/* Title Section */}
        <Box 
          sx={{ textAlign: 'center', mb: 6 }}
        >
          <Typography 
            variant="h2" 
            className="ds-title"
            sx={{ 
              fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
              background: 'linear-gradient(135deg, #4F46E5 0%, #818CF8 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              textFillColor: 'transparent',
              fontWeight: 800,
              mb: 2,
              letterSpacing: '-0.02em',
              position: 'relative',
              display: 'inline-block',
            }}
          >
            Calculate Your Upgrade Value
          </Typography>
          
          <Typography 
            variant="h5" 
            className="ds-subtitle"
            sx={{ 
              color: '#333',
              mb: 4,
              maxWidth: '700px',
              mx: 'auto',
              fontWeight: 400,
              fontSize: { xs: '1rem', sm: '1.1rem', md: '1.25rem' },
              lineHeight: 1.6
            }}
          >
            Discover the premium benefits you've been missing with budget carriers
          </Typography>
        </Box>

        {/* Main Calculator Paper */}
        <Paper 
          className="ds-calculator-paper"
          elevation={0} 
          sx={{ 
            p: { xs: 2, sm: 3, md: 4 }, 
            borderRadius: { xs: 3, sm: 4, md: 6 },
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.8)',
            boxShadow: '0 20px 80px rgba(0, 0, 0, 0.07), 0 2px 8px rgba(0, 0, 0, 0.04), 0 0 0 1px rgba(255, 255, 255, 0.95) inset',
            position: 'relative',
            overflow: 'hidden',
            zIndex: 2,
          }}
        >
          {/* Back Button */}
          <Button
            startIcon={<ArrowLeft size={16} />}
            onClick={onBack || (() => navigate('/'))}
            variant="text"
            className="ds-back-button"
            sx={{ color: '#555', mb: 3, fontWeight: 500 }}
          >
            Back to options
          </Button>

          {/* Current MVNO Plan Selector */}
          <Box 
            sx={{ 
              background: 'rgba(255, 255, 255, 0.8)',
              backdropFilter: 'blur(10px)',
              p: { xs: 2, sm: 3, md: 4 }, 
              borderRadius: 4, 
              mb: 4,
              border: '1px solid rgba(79, 70, 229, 0.08)',
              boxShadow: '0 8px 20px rgba(0, 0, 0, 0.03)',
              position: 'relative',
              overflow: 'hidden'
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#222', mb: 3 }}>
              Your Current Budget Plan:
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, mb: 1 }}>
                  Select Budget Carrier:
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {Object.keys(MVNO_PLANS).map((mvno) => (
                    <Button 
                      key={mvno}
                      onClick={() => handleMvnoSelect(mvno)}
                      variant={selectedMvno === mvno ? "contained" : "outlined"}
                      className={`ds-select-button ${selectedMvno === mvno ? 'selected' : ''} ds-select-button--mvno`}
                      sx={{ 
                        textTransform: 'capitalize',
                        bgcolor: selectedMvno === mvno ? '#4F46E5' : 'transparent',
                        borderColor: 'rgba(79, 70, 229, 0.3)',
                        color: selectedMvno === mvno ? 'white' : '#333',
                        '&:hover': {
                          bgcolor: selectedMvno === mvno ? '#4338CA' : 'rgba(79, 70, 229, 0.05)',
                          borderColor: 'rgba(79, 70, 229, 0.5)',
                        },
                        mb: 1
                      }}
                    >
                      {mvno === 'mint' ? 'Mint Mobile' : 
                      mvno === 'cricket' ? 'Cricket' : 
                      mvno === 'visible' ? 'Visible' : 
                      mvno === 'boost' ? 'Boost Mobile' :
                      mvno === 'metro' ? 'Metro by T-Mobile' :
                      mvno === 'googlefi' ? 'Google Fi' :
                      mvno === 'usmobile' ? 'US Mobile' : 
                      'Consumer Cellular'}
                    </Button>
                  ))}
                </Box>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, mb: 1 }}>
                  Select Plan:
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {MVNO_PLANS[selectedMvno].map((plan) => (
                    <Button 
                      key={plan.name}
                      onClick={() => setSelectedMvnoPlan(plan.name)}
                      variant={selectedMvnoPlan === plan.name ? "contained" : "outlined"}
                      size="small"
                      sx={{ 
                        bgcolor: selectedMvnoPlan === plan.name ? '#4F46E5' : 'transparent',
                        borderColor: 'rgba(79, 70, 229, 0.3)',
                        color: selectedMvnoPlan === plan.name ? 'white' : '#333',
                        '&:hover': {
                          bgcolor: selectedMvnoPlan === plan.name ? '#4338CA' : 'rgba(79, 70, 229, 0.05)',
                          borderColor: 'rgba(79, 70, 229, 0.5)',
                        },
                        mb: 1
                      }}
                    >
                      {plan.name}
                    </Button>
                  ))}
                </Box>
              </Grid>
            </Grid>
          </Box>

          {/* Premium Plan Selector */}
          <Box 
            sx={{ 
              background: 'rgba(255, 255, 255, 0.8)',
              backdropFilter: 'blur(10px)',
              p: { xs: 2, sm: 3, md: 4 }, 
              borderRadius: 4, 
              mb: 5,
              border: '1px solid rgba(79, 70, 229, 0.15)',
              boxShadow: '0 8px 20px rgba(0, 0, 0, 0.03)',
              position: 'relative',
              overflow: 'hidden'
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#222', mb: 3 }}>
              Premium Plan with CircledIn:
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, mb: 1 }}>
                  Select Premium Carrier:
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {Object.keys(CARRIER_PLANS).map((carrier) => (
                    <Button 
                      key={carrier}
                      onClick={() => handleCarrierSelect(carrier)}
                      variant={selectedPremiumCarrier === carrier ? "contained" : "outlined"}
                      sx={{ 
                        textTransform: 'capitalize',
                        bgcolor: selectedPremiumCarrier === carrier ? '#4F46E5' : 'transparent',
                        borderColor: 'rgba(79, 70, 229, 0.3)',
                        color: selectedPremiumCarrier === carrier ? 'white' : '#333',
                        '&:hover': {
                          bgcolor: selectedPremiumCarrier === carrier ? '#4338CA' : 'rgba(79, 70, 229, 0.05)',
                          borderColor: 'rgba(79, 70, 229, 0.5)',
                        },
                        mb: 1
                      }}
                    >
                      {carrier}
                    </Button>
                  ))}
                </Box>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, mb: 1 }}>
                  Select Plan:
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {CARRIER_PLANS[selectedPremiumCarrier].map((plan) => (
                    <Button 
                      key={plan.name}
                      onClick={() => setSelectedPremiumPlan(plan.name)}
                      variant={selectedPremiumPlan === plan.name ? "contained" : "outlined"}
                      size="small"
                      sx={{ 
                        bgcolor: selectedPremiumPlan === plan.name ? '#4F46E5' : 'transparent',
                        borderColor: 'rgba(79, 70, 229, 0.3)',
                        color: selectedPremiumPlan === plan.name ? 'white' : '#333',
                        '&:hover': {
                          bgcolor: selectedPremiumPlan === plan.name ? '#4338CA' : 'rgba(79, 70, 229, 0.05)',
                          borderColor: 'rgba(79, 70, 229, 0.5)',
                        },
                        mb: 1
                      }}
                    >
                      {plan.name}
                    </Button>
                  ))}
                </Box>
              </Grid>
            </Grid>
          </Box>

          {/* Value Comparison Cards - Side by Side */}
          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 3,
            mb: 5
          }}>
            <Fade in={animateCards} timeout={800} style={{ transitionDelay: '100ms', flex: 1 }}>
              <Box sx={{ width: '100%' }}>
                <InfoCard variant="mvno">
                  <Box sx={{ position: 'relative' }}>
                    <Box sx={{ 
                      position: 'absolute',
                      top: -8,
                      right: -8,
                      bgcolor: '#6B7280',
                      color: 'white',
                      width: 36,
                      height: 36,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: '50%'
                    }}>
                      <Tag size={18} />
                    </Box>

                    <Typography variant="h6" color="text.primary" sx={{ mb: 3, fontWeight: 600 }}>
                      Your Current Plan
                    </Typography>

                    <Box sx={{ mb: 3 }}>
                      <Typography variant="body2" color="text.secondary">Monthly Price</Typography>
                      <Typography variant="h4" color="text.primary" sx={{ fontWeight: 700 }}>
                        ${getCurrentMvnoPlan().price}
                      </Typography>
                    </Box>

                    <Divider sx={{ my: 2 }} />

                    <FeatureItem 
                      icon={Wifi} 
                      text={`Data: ${getCurrentMvnoPlan().data}`} 
                      color="#6B7280"
                      bgColor="rgba(107, 114, 128, 0.1)"
                    />
                    <FeatureItem 
                      icon={Globe} 
                      text={`Hotspot: ${getCurrentMvnoPlan().hotspot}`} 
                      color="#6B7280"
                      bgColor="rgba(107, 114, 128, 0.1)"
                    />
                    <FeatureItem 
                      icon={Shield} 
                      text={`Network: ${getCurrentMvnoPlan().priorityData}`} 
                      color="#6B7280"
                      bgColor="rgba(107, 114, 128, 0.1)"
                    />

                    <Box sx={{ 
                      mt: 3, 
                      p: 2, 
                      bgcolor: 'rgba(107, 114, 128, 0.1)', 
                      borderRadius: 2,
                      color: 'text.secondary',
                      fontSize: '0.875rem',
                      textAlign: 'center'
                    }}>
                      No perks or premium network priority
                    </Box>
                  </Box>
                </InfoCard>
              </Box>
            </Fade>
            
            <Fade in={animateCards} timeout={800} style={{ transitionDelay: '300ms', flex: 2 }}>
              <Box sx={{ width: '100%' }}>
                <InfoCard variant="premium" className="premium-card">
                  <Box sx={{ position: 'relative' }}>
                    <Box sx={{ 
                      position: 'absolute',
                      top: -8,
                      right: -8,
                      bgcolor: 'white',
                      color: '#4F46E5',
                      width: 36,
                      height: 36,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: '50%'
                    }}>
                      <CheckCircle size={18} />
                    </Box>

                    <Typography variant="h6" color="white" sx={{ mb: 3, fontWeight: 600 }}>
                      Premium Plan with CircledIn
                    </Typography>

                    <Grid container spacing={3}>
                      <Grid item xs={12} md={6}>
                        <Box sx={{ mb: 3 }}>
                          <Typography variant="body2" className="premium-price-label">Monthly Price</Typography>
                          <Typography variant="h4" className="premium-price">
                            ${getCurrentPremiumPlan().bestGroupPrice}
                          </Typography>
                          <Typography variant="body2" className="premium-comparison">
                            {getMonthlyDifference() > 0 ? (
                              <span>+${getMonthlyDifference()} compared to your current plan</span>
                            ) : (
                              <span className="ds-text-flash">You'll save ${Math.abs(getMonthlyDifference())} per month!</span>
                            )}
                          </Typography>
                        </Box>

                        <Divider sx={{ my: 2, borderColor: 'rgba(255,255,255,0.2)' }} />

                        <FeatureItem 
                          icon={Wifi} 
                          text={`Data: ${getCurrentPremiumPlan().data}`} 
                          color="white"
                          bgColor="rgba(255,255,255,0.2)"
                        />
                        <FeatureItem 
                          icon={Globe} 
                          text={`Hotspot: ${getCurrentPremiumPlan().hotspot}`} 
                          color="white"
                          bgColor="rgba(255,255,255,0.2)"
                        />
                        <FeatureItem 
                          icon={Shield} 
                          text={`Network: ${getCurrentPremiumPlan().priorityData}`} 
                          color="white"
                          bgColor="rgba(255,255,255,0.2)"
                        />
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <Box sx={{ mb: 3 }}>
                          <Typography variant="body2" className="premium-added-value-label">Added Value</Typography>
                          <Typography variant="h4" className="premium-added-value">
                            +${getMonthlyAddedValue()}/mo
                          </Typography>
                        </Box>

                        <Divider sx={{ my: 2, borderColor: 'rgba(255,255,255,0.2)' }} />

                        <Box className="premium-benefits-box" sx={{ mb: 3 }}>
                          <Typography variant="body2" className="premium-benefits-title">
                            Premium Benefits Included:
                          </Typography>
                          
                          <Grid container spacing={1}>
                            {getCurrentPremiumPlan().perks.slice(0, 6).map((perk, idx) => (
                              <Grid item xs={12} sm={6} key={idx}>
                                <Box className="premium-benefit-item">
                                  <CheckCircle size={14} />
                                  <Typography variant="body2" fontSize="0.75rem">
                                    {perk}
                                  </Typography>
                                </Box>
                              </Grid>
                            ))}
                          </Grid>

                          {getCurrentPremiumPlan().perks.length > 6 && (
                            <Button 
                              size="small" 
                              sx={{ 
                                color: 'white', 
                                textDecoration: 'underline',
                                mt: 1,
                                p: 0,
                                minWidth: 'auto',
                                fontSize: '0.75rem'
                              }}
                              onClick={() => setShowValueDetails(!showValueDetails)}
                            >
                              {showValueDetails ? 'Show less' : `+${getCurrentPremiumPlan().perks.length - 6} more benefits`}
                            </Button>
                          )}
                        </Box>

                        <Box className="value-ratio-box" sx={{
                          backgroundColor: 'rgba(255, 255, 255, 0.15) !important',
                          border: '1px solid rgba(255, 255, 255, 0.3)'
                        }}>
                          <Typography variant="body2" className="value-ratio-label" sx={{ color: 'white !important' }}>Value-to-Price Ratio</Typography>
                          <Typography variant="h4" className="value-ratio" sx={{ color: 'white !important' }}>
                            {getValueRatio()}x
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>
                </InfoCard>
              </Box>
            </Fade>
          </Box>

          {/* Value Details Expandable Section */}
          <Collapse in={showValueDetails} timeout={800}>
            <Box 
              sx={{ 
                mb: 5, 
                p: 4, 
                borderRadius: 4, 
                border: '1px solid rgba(79, 70, 229, 0.15)',
                bgcolor: 'rgba(79, 70, 229, 0.03)'
              }}
            >
              <Typography variant="h5" sx={{ mb: 3, fontWeight: 700, color: '#4F46E5' }}>
                Complete Value Breakdown
              </Typography>

              <Grid container spacing={4}>
                <Grid item xs={12} md={5}>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#333' }}>Features Comparison</Typography>
                  
                  <Box 
                    sx={{ 
                      p: 3, 
                      bgcolor: 'white', 
                      borderRadius: 3,
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)'
                    }}
                  >
                    {/* Data Quality Comparison */}
                    <Box sx={{ mb: 3 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2" fontWeight="600" color="#333">Data Quality</Typography>
                        <Box sx={{ display: 'flex', gap: 4 }}>
                          <Box sx={{ textAlign: 'center' }}>
                            <Box 
                              className="ds-progress-container"
                              sx={{ 
                                width: 80, 
                                height: 4, 
                                bgcolor: 'rgba(107, 114, 128, 0.2)',
                                borderRadius: 2,
                                overflow: 'hidden',
                                mb: 0.5
                              }}
                            >
                              <Box 
                                className="ds-progress-bar ds-progress-bar--mvno"
                                sx={{ 
                                  width: '40%', 
                                  height: '100%', 
                                  bgcolor: '#6B7280',
                                  borderRadius: 2
                                }}
                              />
                            </Box>
                            <Typography variant="caption" color="text.secondary">Basic</Typography>
                          </Box>
                          <Box sx={{ textAlign: 'center' }}>
                            <Box 
                              className="ds-progress-container"
                              sx={{ 
                                width: 80, 
                                height: 4, 
                                bgcolor: 'rgba(79, 70, 229, 0.2)',
                                borderRadius: 2,
                                overflow: 'hidden',
                                mb: 0.5
                              }}
                            >
                              <Box 
                                className="ds-progress-bar ds-progress-bar--premium"
                                sx={{ 
                                  width: '90%', 
                                  height: '100%', 
                                  bgcolor: '#4F46E5',
                                  borderRadius: 2
                                }}
                              />
                            </Box>
                            <Typography variant="caption" color="#4F46E5">Premium</Typography>
                          </Box>
                        </Box>
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        Premium carriers provide priority data with higher speeds during congestion
                      </Typography>
                    </Box>

                    {/* Coverage Comparison */}
                    <Box sx={{ mb: 3 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2" fontWeight="600" color="#333">Coverage</Typography>
                        <Box sx={{ display: 'flex', gap: 4 }}>
                          <Box sx={{ textAlign: 'center' }}>
                            <Box 
                              className="ds-progress-container"
                              sx={{ 
                                width: 80, 
                                height: 4, 
                                bgcolor: 'rgba(107, 114, 128, 0.2)',
                                borderRadius: 2,
                                overflow: 'hidden',
                                mb: 0.5
                              }}
                            >
                              <Box 
                                className="ds-progress-bar ds-progress-bar--mvno"
                                sx={{ 
                                  width: '60%', 
                                  height: '100%', 
                                  bgcolor: '#6B7280',
                                  borderRadius: 2
                                }}
                              />
                            </Box>
                            <Typography variant="caption" color="text.secondary">Limited</Typography>
                          </Box>
                          <Box sx={{ textAlign: 'center' }}>
                            <Box 
                              className="ds-progress-container"
                              sx={{ 
                                width: 80, 
                                height: 4, 
                                bgcolor: 'rgba(79, 70, 229, 0.2)',
                                borderRadius: 2,
                                overflow: 'hidden',
                                mb: 0.5
                              }}
                            >
                              <Box 
                                className="ds-progress-bar ds-progress-bar--premium"
                                sx={{ 
                                  width: '95%', 
                                  height: '100%', 
                                  bgcolor: '#4F46E5',
                                  borderRadius: 2
                                }}
                              />
                            </Box>
                            <Typography variant="caption" color="#4F46E5">Extensive</Typography>
                          </Box>
                        </Box>
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        Premium carriers have better overall coverage and indoor reception
                      </Typography>
                    </Box>

                    {/* Customer Support Comparison */}
                    <Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2" fontWeight="600" color="#333">Customer Service</Typography>
                        <Box sx={{ display: 'flex', gap: 4 }}>
                          <Box sx={{ textAlign: 'center' }}>
                            <Box 
                              className="ds-progress-container"
                              sx={{ 
                                width: 80, 
                                height: 4, 
                                bgcolor: 'rgba(107, 114, 128, 0.2)',
                                borderRadius: 2,
                                overflow: 'hidden',
                                mb: 0.5
                              }}
                            >
                              <Box 
                                className="ds-progress-bar ds-progress-bar--mvno"
                                sx={{ 
                                  width: '30%', 
                                  height: '100%', 
                                  bgcolor: '#6B7280',
                                  borderRadius: 2
                                }}
                              />
                            </Box>
                            <Typography variant="caption" color="text.secondary">Chat Only</Typography>
                          </Box>
                          <Box sx={{ textAlign: 'center' }}>
                            <Box 
                              className="ds-progress-container"
                              sx={{ 
                                width: 80, 
                                height: 4, 
                                bgcolor: 'rgba(79, 70, 229, 0.2)',
                                borderRadius: 2,
                                overflow: 'hidden',
                                mb: 0.5
                              }}
                            >
                              <Box 
                                className="ds-progress-bar ds-progress-bar--premium"
                                sx={{ 
                                  width: '95%', 
                                  height: '100%', 
                                  bgcolor: '#4F46E5',
                                  borderRadius: 2
                                }}
                              />
                            </Box>
                            <Typography variant="caption" color="#4F46E5">Stores & 24/7</Typography>
                          </Box>
                        </Box>
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        Premium carriers offer physical stores and comprehensive support
                      </Typography>
                    </Box>
                  </Box>
                </Grid>

                <Grid item xs={12} md={7}>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#333' }}>Value Calculation</Typography>
                  
                  <Box 
                    sx={{ 
                      p: 3, 
                      bgcolor: 'white', 
                      borderRadius: 3,
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)'
                    }}
                  >
                    <Table size="small" className="ds-value-table">
                      <TableBody>
                        <TableRow>
                          <TableCell><Typography color="#333">Monthly Premium Price</Typography></TableCell>
                          <TableCell align="right"><Typography color="#333">${getCurrentPremiumPlan().bestGroupPrice}</Typography></TableCell>
                        </TableRow>

                        {/* Welcome Credit */}
                        {getCurrentPremiumPlan().perks.some(p => p.includes('welcome credit')) && (
                          <TableRow>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Typography variant="body2" color="#333">Welcome Credit</Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                  (monthly value)
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell align="right" sx={{ color: '#059669', fontWeight: 600 }} className="ds-value-add">
                              +${Math.round(parseInt(getCurrentPremiumPlan().perks.find(p => p.includes('welcome credit')).match(/\$(\d+)/)[1]) / 24)}
                            </TableCell>
                          </TableRow>
                        )}

                        {/* Streaming Services */}
                        {getCurrentPremiumPlan().perks.some(p => 
                          p.includes('Disney+') || 
                          p.includes('Netflix') || 
                          p.includes('HBO Max') || 
                          p.includes('Apple Music') ||
                          p.includes('Hulu') ||
                          p.includes('ESPN+') ||
                          p.includes('Apple TV+') ||
                          p.includes('Paramount+') ||
                          p.includes('Apple One')
                        ) && (
                          <TableRow>
                            <TableCell><Typography color="#333">Streaming Services</Typography></TableCell>
                            <TableCell align="right" sx={{ color: '#059669', fontWeight: 600 }} className="ds-value-add">
                              +${(
                                (getCurrentPremiumPlan().perks.some(p => p.includes('Disney+')) ? 8 : 0) +
                                (getCurrentPremiumPlan().perks.some(p => p.includes('Netflix')) ? 10 : 0) +
                                (getCurrentPremiumPlan().perks.some(p => p.includes('HBO Max')) ? 15 : 0) +
                                (getCurrentPremiumPlan().perks.some(p => p.includes('Apple Music')) ? 10 : 0) +
                                (getCurrentPremiumPlan().perks.some(p => p.includes('Hulu')) ? 8 : 0) +
                                (getCurrentPremiumPlan().perks.some(p => p.includes('ESPN+')) ? 7 : 0) +
                                (getCurrentPremiumPlan().perks.some(p => p.includes('Apple TV+')) ? 7 : 0) +
                                (getCurrentPremiumPlan().perks.some(p => p.includes('Paramount+')) ? 5 : 0) +
                                (getCurrentPremiumPlan().perks.some(p => p.includes('Apple One')) ? 17 : 0)
                              )}
                            </TableCell>
                          </TableRow>
                        )}

                        {/* Network Priority */}
                        <TableRow>
                          <TableCell><Typography color="#333">Premium Network Priority</Typography></TableCell>
                          <TableCell align="right" sx={{ color: '#059669', fontWeight: 600 }} className="ds-value-add">
                            +${getCurrentPremiumPlan().priorityData.includes('Premium') ? 15 : 5}
                          </TableCell>
                        </TableRow>

                        {/* Hotspot */}
                        <TableRow>
                          <TableCell><Typography color="#333">Mobile Hotspot</Typography></TableCell>
                          <TableCell align="right" sx={{ color: '#059669', fontWeight: 600 }} className="ds-value-add">
                            +${Math.min(20, parseInt(getCurrentPremiumPlan().hotspot) / 2 || 0)}
                          </TableCell>
                        </TableRow>

                        {/* International */}
                        <TableRow>
                          <TableCell><Typography color="#333">International Features</Typography></TableCell>
                          <TableCell align="right" sx={{ color: '#059669', fontWeight: 600 }} className="ds-value-add">
                            +${getCurrentPremiumPlan().international.includes('Mexico & Canada') ? 5 : 0}
                            {getCurrentPremiumPlan().international.includes('destinations') ? ' +$15' : ''}
                          </TableCell>
                        </TableRow>

                        {/* Total Value */}
                        <TableRow className="ds-value-table-total" sx={{ bgcolor: 'rgba(79, 70, 229, 0.05)' }}>
                          <TableCell sx={{ fontWeight: 700 }}><Typography color="#333" fontWeight="700">Total Monthly Value</Typography></TableCell>
                          <TableCell align="right" sx={{ color: '#4F46E5', fontWeight: 700 }} className="ds-value-add">
                            ${getTotalMonthlyValue()}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>

                    <Box 
                      sx={{ 
                        mt: 3,
                        p: 3,
                        bgcolor: 'rgba(79, 70, 229, 0.05)',
                        borderRadius: 2,
                        border: '1px solid rgba(79, 70, 229, 0.1)'
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Box>
                          <Typography variant="body2" fontWeight="600" color="#333">Real Cost Comparison</Typography>
                          <Typography variant="caption" color="text.secondary">After accounting for all benefits</Typography>
                        </Box>
                        <Box sx={{ textAlign: 'right' }}>
                          <Typography 
                            variant="h6" 
                            fontWeight="700"
                            sx={{
                              color: getMonthlyDifference() <= 0 ? '#059669' : 'text.primary'
                            }}
                          >
                            {getMonthlyDifference() > 0 ? (
                              <span>+${getMonthlyDifference()}/mo</span>
                            ) : (
                              <span>-${Math.abs(getMonthlyDifference())}/mo</span>
                            )}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Effective monthly difference
                          </Typography>
                        </Box>
                      </Box>

                      <Divider sx={{ my: 2 }} />

                      <Typography 
                        variant="body2" 
                        sx={{
                          fontWeight: 500,
                          color: getMonthlyDifference() <= 0 ? '#059669' : 'text.primary'
                        }}
                      >
                        {getMonthlyDifference() > 0 ? (
                          <span>
                            For just <strong>${getMonthlyDifference()}</strong> more per month, you get <strong>${getMonthlyAddedValue()}</strong> in additional value
                          </span>
                        ) : (
                          <span>
                            You'll actually <strong>SAVE ${Math.abs(getMonthlyDifference())}</strong> per month while getting <strong>${getMonthlyAddedValue()}</strong> in additional value!
                          </span>
                        )}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Collapse>

          {/* Comparison Table */}
          <Fade in={tableLoaded} timeout={1000}>
            <Box 
              className="ds-table-container"
              sx={{ 
                borderRadius: 4, 
                overflow: 'hidden',
                background: 'rgba(255, 255, 255, 0.95)',
                border: '1px solid rgba(0, 0, 0, 0.05)',
                mb: 4,
                boxShadow: '0 8px 20px rgba(0, 0, 0, 0.03)'
              }}
            >
              <TableContainer sx={{ overflowX: 'auto' }}>
                <Table className="ds-table" sx={{ tableLayout: 'fixed' }}>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: 'rgba(249, 250, 251, 0.8)' }}>
                      <TableCell width="25%" sx={{ fontWeight: 600, fontSize: '1rem', color: '#333' }}>
                        Plan
                      </TableCell>
                      <TableCell width="15%" align="right" sx={{ fontWeight: 600, fontSize: '1rem', color: '#333' }}>
                        Monthly Cost
                      </TableCell>
                      <TableCell width="15%" align="right" sx={{ fontWeight: 600, fontSize: '1rem', color: '#333' }}>
                        Added Benefits
                      </TableCell>
                      <TableCell width="15%" align="right" sx={{ fontWeight: 600, fontSize: '1rem', color: '#333' }}>
                        Total Value
                      </TableCell>
                      <TableCell width="15%" align="right" sx={{ fontWeight: 600, fontSize: '1rem', color: '#333' }}>
                        Value Ratio
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {/* Current MVNO Plan */}
                    <StyledTableRow>
                      <StyledTableCell>
                        <Typography fontWeight="600" className="ds-table-carrier">
                          {selectedMvno === 'mint' ? 'Mint Mobile' : 
                          selectedMvno === 'cricket' ? 'Cricket' : 
                          selectedMvno === 'visible' ? 'Visible' : 
                          selectedMvno === 'boost' ? 'Boost Mobile' :
                          selectedMvno === 'metro' ? 'Metro by T-Mobile' :
                          selectedMvno === 'googlefi' ? 'Google Fi' :
                          selectedMvno === 'usmobile' ? 'US Mobile' : 
                          'Consumer Cellular'} - {getCurrentMvnoPlan().name}
                          <Box component="span" className="ds-badge ds-badge--gray" sx={{ 
                            ml: 1, 
                            px: 1, 
                            py: 0.5, 
                            bgcolor: 'rgba(107, 114, 128, 0.1)', 
                            borderRadius: 1,
                            fontSize: '0.7rem',
                            color: '#6B7280',
                            verticalAlign: 'middle'
                          }}>
                            Current
                          </Box>
                        </Typography>
                      </StyledTableCell>
                      <PriceCell align="right">
                        ${getCurrentMvnoPlan().price}
                      </PriceCell>
                      <PriceCell align="right">
                        $0
                      </PriceCell>
                      <PriceCell align="right">
                        ${getCurrentMvnoPlan().price}
                      </PriceCell>
                      <PriceCell align="right">
                        1.0x
                      </PriceCell>
                    </StyledTableRow>

                    {/* Premium Plan */}
                    <StyledTableRow isPlan={true}>
                      <StyledTableCell>
                        <Typography fontWeight="600" className="ds-table-carrier">
                          {selectedPremiumCarrier.charAt(0).toUpperCase() + selectedPremiumCarrier.slice(1)} - {getCurrentPremiumPlan().name}
                          <Box component="span" className="ds-badge ds-badge--indigo" sx={{ 
                            ml: 1, 
                            px: 1, 
                            py: 0.5, 
                            bgcolor: 'rgba(79, 70, 229, 0.1)', 
                            borderRadius: 1,
                            fontSize: '0.7rem',
                            color: '#4F46E5',
                            verticalAlign: 'middle'
                          }}>
                            Premium
                          </Box>
                        </Typography>
                      </StyledTableCell>
                      <PriceCell variant="discount">
                        ${getCurrentPremiumPlan().bestGroupPrice}
                      </PriceCell>
                      <PriceCell variant="discount">
                        +${getMonthlyAddedValue()}
                      </PriceCell>
                      <PriceCell variant="discount">
                        ${getTotalMonthlyValue()}
                      </PriceCell>
                      <PriceCell variant="discount">
                        {getValueRatio()}x
                      </PriceCell>
                    </StyledTableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Fade>

          {/* Note Text */}
          <Typography 
            variant="caption" 
            color="text.secondary"
            className="ds-note-text"
            sx={{ 
              display: 'block', 
              mt: 2, 
              mb: 5,
              textAlign: 'center',
              fontSize: '0.8rem'
            }}
          >
            Note: Value calculations are estimates based on market rates for comparable services and features.
          </Typography>

          {/* CTA */}
          <Box 
            className="ds-cta-section"
            sx={{
              textAlign: 'center',
              p: { xs: 3, sm: 4, md: 5 },
              borderRadius: 4,
              backgroundImage: 'linear-gradient(135deg, rgba(79, 70, 229, 0.05) 0%, rgba(79, 70, 229, 0.1) 100%)',
              border: '1px solid rgba(79, 70, 229, 0.1)',
              position: 'relative',
              overflow: 'hidden'
            }}
          >
            <Typography variant="h3" component="h3" className="ds-cta-title" sx={{ 
              fontWeight: 800, 
              color: '#4F46E5', 
              mb: 2,
              fontSize: { xs: '1.75rem', sm: '2.25rem', md: '2.5rem' } 
            }}>
              Upgrade to Premium Without Breaking the Bank
            </Typography>
            
            <Typography variant="h6" className="ds-cta-subtitle" sx={{ 
              mb: 4, 
              color: '#444', 
              fontWeight: 400,
              fontSize: { xs: '1rem', sm: '1.1rem', md: '1.25rem' }
            }}>
              {getMonthlyDifference() > 0 ? (
                <span>For just <strong>${getMonthlyDifference()}</strong> more per month, get <strong>${getMonthlyAddedValue()}</strong> in premium benefits</span>
              ) : (
                <span>Save <strong>${Math.abs(getMonthlyDifference())}</strong> per month while enjoying premium benefits worth <strong>${getMonthlyAddedValue()}</strong></span>
              )}
            </Typography>
            
            <Button
              variant="contained"
              size="large"
              endIcon={<Sparkles size={20} />}
              onClick={handleViewAllCircles}
              className="ds-cta-button ds-cta-button--indigo"
              sx={{
                bgcolor: '#4F46E5',
                px: { xs: 3, md: 4 },
                py: 1.5,
                borderRadius: 50,
                fontWeight: 600,
                boxShadow: '0 10px 20px rgba(79, 70, 229, 0.25)',
                '&:hover': {
                  bgcolor: '#4338CA',
                }
              }}
            >
              View Available Circles
            </Button>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default DynamicSavingsBudget;